import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiPause, FiCheck, FiX, FiRotateCcw, FiClock } from 'react-icons/fi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TABS = ['pending', 'active', 'completed', 'rejected', 'cancelled'];

const STATUS_BADGE = {
  pending: 'bg-yellow-100 text-yellow-700',
  active: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-700',
};

export default function PauseRequestsTab({ getAuthHeader }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [actioningId, setActioningId] = useState(null);
  const [rejectModal, setRejectModal] = useState(null); // { id }
  const [rejectReason, setRejectReason] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/pause-requests${filter ? `?status_filter=${filter}` : ''}`,
        { headers: getAuthHeader() }
      );
      setItems(res.data || []);
    } catch (err) {
      console.error('pause requests fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, getAuthHeader]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this pause? The member must have 0 borrowed books.')) return;
    setActioningId(id);
    try {
      const res = await axios.post(`${API}/admin/pause-requests/${id}/approve`, {}, { headers: getAuthHeader() });
      if (res.data?.razorpay_error) {
        alert(`Approved, but Razorpay pause failed: ${res.data.razorpay_error}. Please pause manually in the Razorpay dashboard.`);
      } else {
        alert('Pause approved and active.');
      }
      await fetchItems();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve pause');
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActioningId(rejectModal.id);
    try {
      await axios.post(
        `${API}/admin/pause-requests/${rejectModal.id}/reject`,
        { reason: rejectReason },
        { headers: getAuthHeader() }
      );
      setRejectModal(null);
      setRejectReason('');
      await fetchItems();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reject pause');
    } finally {
      setActioningId(null);
    }
  };

  const handleResumeNow = async (id) => {
    if (!window.confirm('Resume this pause immediately? Razorpay will resume billing.')) return;
    setActioningId(id);
    try {
      const res = await axios.post(`${API}/admin/pause-requests/${id}/resume-now`, {}, { headers: getAuthHeader() });
      if (res.data?.razorpay_error) {
        alert(`Marked complete, but Razorpay resume failed: ${res.data.razorpay_error}. Please resume manually.`);
      } else {
        alert('Subscription resumed.');
      }
      await fetchItems();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to resume pause');
    } finally {
      setActioningId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="pause-requests-tab">
      <div className="flex items-center gap-3">
        <FiPause className="w-6 h-6 text-indigo-600" />
        <h2 className="text-2xl font-bold text-gray-800">Pause Requests</h2>
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors capitalize ${
              filter === s ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-testid={`pause-filter-${s}`}
          >{s}</button>
        ))}
        <button
          onClick={() => setFilter('')}
          className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
            filter === '' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="pause-filter-all"
        >All</button>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">Loading...</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
          No {filter || 'pause'} requests.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-3 px-4">Member</th>
                <th className="py-3 px-4">Plan</th>
                <th className="py-3 px-4">Dates</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t hover:bg-gray-50" data-testid={`pause-row-${p.id}`}>
                  <td className="py-3 px-4">
                    <p className="font-medium text-gray-800">{p.user_name}</p>
                    <p className="text-xs text-gray-500">{p.user_email}</p>
                    {p.reason && <p className="text-xs text-gray-500 mt-1 italic">"{p.reason}"</p>}
                    {p.rejected_reason && <p className="text-xs text-red-600 mt-1">Rejected: {p.rejected_reason}</p>}
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                      {p.plan_mode === 'autopay' ? <><FiClock className="w-3 h-3" /> Autopay</> : 'One-time'}
                      <span className="capitalize">· {p.plan_type}</span>
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-gray-800">
                      {new Date(p.start_date).toLocaleDateString()} → {new Date(p.end_date).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-gray-500">{p.duration_days} days · requested {new Date(p.requested_at).toLocaleDateString()}</p>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_BADGE[p.status] || 'bg-gray-100 text-gray-700'}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-2">
                      {p.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(p.id)}
                            disabled={actioningId === p.id}
                            className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                            data-testid={`approve-pause-${p.id}`}
                          >
                            <FiCheck className="w-3 h-3" /> Approve
                          </button>
                          <button
                            onClick={() => { setRejectModal({ id: p.id }); setRejectReason(''); }}
                            disabled={actioningId === p.id}
                            className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                            data-testid={`reject-pause-${p.id}`}
                          >
                            <FiX className="w-3 h-3" /> Reject
                          </button>
                        </>
                      )}
                      {p.status === 'active' && (
                        <button
                          onClick={() => handleResumeNow(p.id)}
                          disabled={actioningId === p.id}
                          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                          data-testid={`resume-pause-${p.id}`}
                        >
                          <FiRotateCcw className="w-3 h-3" /> Resume now
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="reject-pause-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-5 border-b">
              <h3 className="text-lg font-bold text-gray-800">Reject pause request</h3>
            </div>
            <div className="p-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full border rounded-lg p-2 text-sm"
                placeholder="Why is this pause being rejected?"
                data-testid="reject-reason-input"
              />
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <button
                onClick={() => setRejectModal(null)}
                className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700"
              >Cancel</button>
              <button
                onClick={handleReject}
                disabled={!!actioningId}
                className="px-4 py-2 bg-red-600 text-white rounded text-sm font-semibold disabled:opacity-50"
                data-testid="confirm-reject-pause-btn"
              >Confirm reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
